'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';

export type FontSize = 'normal' | 'large' | 'xlarge';
export type ContrastMode = 'normal' | 'high';

interface AccessibilityPreferences {
  fontSize: FontSize;
  highContrast: ContrastMode;
  reduceMotion: boolean;
  screenReaderMode: boolean;
  focusIndicators: boolean;
  dyslexicFont: boolean;
}

interface AccessibilityContextValue extends AccessibilityPreferences {
  setFontSize: (size: FontSize) => void;
  setHighContrast: (mode: ContrastMode) => void;
  setReduceMotion: (val: boolean) => void;
  setScreenReaderMode: (val: boolean) => void;
  setFocusIndicators: (val: boolean) => void;
  setDyslexicFont: (val: boolean) => void;
  resetAll: () => void;
}

const DEFAULT_PREFS: AccessibilityPreferences = {
  fontSize: 'normal',
  highContrast: 'normal',
  reduceMotion: false,
  screenReaderMode: false,
  focusIndicators: true,
  dyslexicFont: false,
};

const AccessibilityContext = createContext<AccessibilityContextValue>({
  ...DEFAULT_PREFS,
  setFontSize: () => {},
  setHighContrast: () => {},
  setReduceMotion: () => {},
  setScreenReaderMode: () => {},
  setFocusIndicators: () => {},
  setDyslexicFont: () => {},
  resetAll: () => {},
});

export const useAccessibility = () => useContext(AccessibilityContext);

const STORAGE_KEY = 'app-accessibility';

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const [prefs, setPrefs] = useState<AccessibilityPreferences>(DEFAULT_PREFS);

  // Load from localStorage + detect system reduce-motion
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const parsed: Partial<AccessibilityPreferences> = saved ? JSON.parse(saved) : {};
    // Detect system preference for reduced motion
    const systemReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const merged: AccessibilityPreferences = {
      ...DEFAULT_PREFS,
      ...parsed,
      reduceMotion: parsed.reduceMotion !== undefined ? parsed.reduceMotion : systemReduceMotion,
    };
    setPrefs(merged);
    applyPrefs(merged);
  }, []);

  const applyPrefs = (p: AccessibilityPreferences) => {
    const root = document.documentElement;

    // Font size
    root.classList.remove('text-size-normal', 'text-size-large', 'text-size-xlarge');
    root.classList.add(`text-size-${p.fontSize}`);

    // High contrast
    if (p.highContrast === 'high') {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Reduce motion
    if (p.reduceMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }

    // Focus indicators
    if (p.focusIndicators) {
      root.classList.add('enhanced-focus');
    } else {
      root.classList.remove('enhanced-focus');
    }

    // Dyslexic font
    if (p.dyslexicFont) {
      root.classList.add('dyslexic-font');
    } else {
      root.classList.remove('dyslexic-font');
    }

    // Screen reader mode — adds aria-live region hint
    if (p.screenReaderMode) {
      root.setAttribute('data-screen-reader', 'true');
    } else {
      root.removeAttribute('data-screen-reader');
    }
  };

  const save = (updated: AccessibilityPreferences) => {
    setPrefs(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    applyPrefs(updated);
  };

  const setFontSize = (fontSize: FontSize) => save({ ...prefs, fontSize });
  const setHighContrast = (highContrast: ContrastMode) => save({ ...prefs, highContrast });
  const setReduceMotion = (reduceMotion: boolean) => save({ ...prefs, reduceMotion });
  const setScreenReaderMode = (screenReaderMode: boolean) => save({ ...prefs, screenReaderMode });
  const setFocusIndicators = (focusIndicators: boolean) => save({ ...prefs, focusIndicators });
  const setDyslexicFont = (dyslexicFont: boolean) => save({ ...prefs, dyslexicFont });
  const resetAll = () => save(DEFAULT_PREFS);

  return (
    <AccessibilityContext.Provider value={{
      ...prefs,
      setFontSize,
      setHighContrast,
      setReduceMotion,
      setScreenReaderMode,
      setFocusIndicators,
      setDyslexicFont,
      resetAll,
    }}>
      {children}
    </AccessibilityContext.Provider>
  );
}
